import { getUserId } from '../../lib/auth-session';
import { fetchEntityPage } from '../../lib/entity-api';
import { getApiUrl } from '../../lib/api-config';
import { Notification } from './types';

export interface PaginatedNotifications {
  notifications: Notification[];
  hasMore: boolean;
  totalCount: number;
}

export class NotificationsService {
  private static readonly PAGE_SIZE = 20;

  /**
   * Fetch the current user's in-app notifications, one page at a time: the backend (generic entity list `notification`, always scoped to
   * the acting user) cuts the page and returns the exact total -- nothing is paged or counted in the browser.
   */
  static async getNotifications(limit?: number, offset: number = 0): Promise<PaginatedNotifications> {
    try {
      const page = await fetchEntityPage<any>('notification', {}, { limit: limit || this.PAGE_SIZE, offset });
      const notifications = page.rows.map((notification) => ({
        ...notification,
        event_name: notification.event?.name || 'Unknown Event',
      }));
      return { notifications, hasMore: page.hasMore, totalCount: page.total };
    } catch (error) {
      console.error('NotificationsService.getNotifications error:', error);
      return {
        notifications: [],
        hasMore: false,
        totalCount: 0
      };
    }
  }

  /**
   * Fetch notifications for dropdown (last 4)
   */
  static async getRecentNotifications(): Promise<Notification[]> {
    const result = await this.getNotifications(4, 0);
    return result.notifications;
  }

  /**
   * Mark a notification as read
   */
  static async markAsRead(notificationId: string): Promise<boolean> {
    try {
      const userId = getUserId();

      if (!userId) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(getApiUrl(`api/entities/notification/${notificationId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_read: true,
          read_at: new Date().toISOString()
        })
      });

      if (!response.ok) {
        console.error('Error marking notification as read');
        return false;
      }

      return true;
    } catch (error) {
      console.error('NotificationsService.markAsRead error:', error);
      return false;
    }
  }

  /**
   * Mark a notification as unread
   */
  static async markAsUnread(notificationId: string): Promise<boolean> {
    try {
      const userId = getUserId();

      if (!userId) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(getApiUrl(`api/entities/notification/${notificationId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_read: false,
          read_at: null
        })
      });

      if (!response.ok) {
        console.error('Error marking notification as unread');
        return false;
      }

      return true;
    } catch (error) {
      console.error('NotificationsService.markAsUnread error:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read
   */
  static async markAllAsRead(): Promise<boolean> {
    try {
      const response = await fetch(getApiUrl('api/notifications/mark-all-read'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        console.error('Error marking all notifications as read');
        return false;
      }

      return true;
    } catch (error) {
      console.error('NotificationsService.markAllAsRead error:', error);
      return false;
    }
  }

  /**
   * Get count of unread notifications
   */
  static async getUnreadCount(): Promise<number> {
    try {
      // The server counts them: one row asked for, the exact total of the unread ones comes back
      const page = await fetchEntityPage('notification', { filters: { is_read: 'false' } }, { limit: 1, offset: 0 });
      return page.total;
    } catch (error) {
      console.error('NotificationsService.getUnreadCount error:', error);
      return 0;
    }
  }
}
